import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import AuthLayout from "./AuthPageLayout";
import { useAuth } from "../../context/AuthContext";
import axios from "../../lib/axios";
import ReactivateOtpModal from "../../components/auth/ReactivateOtpModal";
import Loader from "../../components/common/Loader";

export default function AccountReactivate() {
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeactivationStatus();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 300);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && userData?.reactivated) {
      navigate("/");
    }
  }, [countdown, userData, navigate]);

  // If user is not deactivated, redirect to next step instead of showing reactivate screen
  useEffect(() => {
    if (!loading && userData && !userData.deactivated_until) {
      // User is not deactivated, redirect to appropriate next step
      navigate("/");
    }
  }, [loading, userData, navigate]);

  const fetchDeactivationStatus = async () => {
    try {
      const response = await axios.get("/deactivation-status");
      if (response.data.success) {
        setUserData(response.data.data);
        
        // Calculate days until reactivation
        if (response.data.data.deactivated_until) {
          const deactivatedUntil = new Date(response.data.data.deactivated_until);
          const now = new Date();
          const diffTime = deactivatedUntil.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setUserData(prev => ({ ...prev, daysUntil: diffDays }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch deactivation status:", error);
      // If user is not actually deactivated, redirect to dashboard
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateClick = () => {
    setShowOtpModal(true);
  };

  const handleOtpSuccess = () => {
    setShowOtpModal(false);
    setUserData(prev => ({ ...prev, reactivated: true }));
    setCountdown(3); // Start countdown for redirect
  };

  const handleBackToLogin = async () => {
      const currentPath = window.location.pathname + window.location.search;
      await logout(currentPath);
      navigate("/signin");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-[#080f1c]">
        <Loader className="w-20 h-20"/>
      </div>
    );
  }

  if (userData?.reactivated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--okbg)] rounded-full mb-4">
              <svg className="w-8 h-8 text-[var(--okt)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Account Reactivated!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your account has been successfully reactivated.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Redirecting to dashboard in {countdown}...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Account Reactivated - Neo"
        description="Your account is temporarily deactivated. Reactivate your account to continue using Neo."
      />
      <AuthLayout hideSignupHighlight>
        <div className="relative w-full dark:bg-[#080f1c]">
          <img
            src="/images/shape/grid-01.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none fixed right-0 top-0 w-[450px] xl:w-[750px] opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
            style={{ zIndex: 0 }}
          />
          <img
            src="/images/shape/grid-01.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none fixed left-0 bottom-0 w-[450px] xl:w-[750px] rotate-180 opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
            style={{ zIndex: 0 }}
          />
          
          <div className="flex justify-center sm:mt-10 sm:mb-10 mt-2 mb-2">
            {/* Light Mode Logo */}
            <img
              className="w-[120px] h-auto sm:w-[200px] sm:h-[42px] block dark:hidden"
              src="/images/neo-logo/neo-logo.svg"
              alt="Logo"
            />

            {/* Dark Mode Logo */}
            <img
              className="w-[120px] h-auto sm:w-[200px] sm:h-[42px] hidden dark:block"
              src="/images/neo-logo/neo-logo.svg"
              alt="Logo"
            />
          </div>

          <div
            className="relative flex flex-col justify-center flex-1 w-full max-w-md mx-auto dark:bg-[#0d1828] p-6 rounded-md border border-gray-100 dark:border-[#FFFFFF0F] shadow-theme-lg"
            style={{ zIndex: 3 }}
          >
            <div className="text-center">
              {/* User Avatar */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">
                    {userData?.name ? getUserInitials(userData.name) : "U"}
                  </span>
                </div>
              </div>

              {/* User Info */}
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90 mb-2">
                  {userData?.name || "User"}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {userData?.email || ""}
                </p>
              </div>

              {/* Deactivation Notice */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  Your account for <span className="font-semibold">{userData?.shop_name || "Neo"}</span> has been temporarily deactivated.
                </h3>
                {/* <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                  Your account for <span className="font-semibold">{userData?.shop_name || "Neo"}</span> has been temporarily deactivated.
                </p> */}
                
                {userData?.deactivated_until && (
                  <div className="bg-white dark:bg-gray-800 rounded-md p-3 border border-amber-200 dark:border-amber-700">
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">
                      DEACTIVATED UNTIL
                    </p>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                      {formatDate(userData.deactivated_until)}
                    </p>
                    {userData.daysUntil !== undefined && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        {userData.daysUntil > 0 ? `${userData.daysUntil} days remaining` : "Reactivation available today"}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleReactivateClick}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-white transition rounded-[6px] bg-[var(--accent)] hover:bg-[var(--indigo2)] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                >
                  Reactivate Account
                </button>
                
                <button
                  onClick={handleBackToLogin}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 transition rounded-[6px] bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Back to Login
                </button>
              </div>

              {/* Help Text */}
              {/* <p className="text-xs text-gray-500 dark:text-gray-400 mt-7">
                If you didn't request this deactivation, please contact our support team.
              </p> */}
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-7 leading-relaxed">
                If you didn't request this deactivation, please contact our support team.
              </p>
            </div>
          </div>
        </div>
      </AuthLayout>

      {/* OTP Modal */}
      {showOtpModal && (
        <ReactivateOtpModal
          isOpen={showOtpModal}
          onClose={() => setShowOtpModal(false)}
          onSuccess={handleOtpSuccess}
          userData={userData}
        />
      )}
    </>
  );
}
